import { gql } from "@apollo/client";

// === SUBSCRIPTION QUERIES ===
export const GET_SUBSCRIPTIONS = gql`
  query GetSubscriptions {
    subscriptions {
      id
      client {
        id
        fName
        lName
        email
      }
      plan {
        name
        description
        amount
        interval
        tier
        includedModules
      }
      status
      startDate
      nextBillingDate
      # Server-derived, Brisbane-anchored. Selected here so the LIST and the
      # DETAIL page can never disagree about whether something is overdue —
      # computing it from nextBillingDate in the browser ignores whether the
      # period was already billed or paid, and paints settled months red.
      billingState
      dueDayBrisbane
      daysOverdue
      billingMode
      trialEndsAt
      canceledAt
      stripeCustomerId
      notes
      createdAt
    }
  }
`;

export const GET_SUBSCRIPTION = gql`
  query GetSubscription($id: ID!) {
    subscription(id: $id) {
      id
      client {
        id
        fName
        lName
        email
        phoneNumber
        businessName
      }
      plan {
        name
        description
        amount
        interval
        tier
        includedModules
        maxUsers
        maxClients
      }
      status
      startDate
      endDate
      nextBillingDate
      trialEndsAt
      canceledAt
      pausedAt
      cancellationReason
      notes
      createdAt
      updatedAt
    }
  }
`;

export const GET_SUBSCRIPTIONS_BY_CLIENT = gql`
  query GetSubscriptionsByClient($clientId: ID!) {
    subscriptionsByClient(clientId: $clientId) {
      id
      plan {
        name
        amount
        interval
      }
      status
      startDate
      nextBillingDate
      createdAt
    }
  }
`;

// === SUBSCRIPTION MUTATIONS ===
export const CREATE_SUBSCRIPTION = gql`
  mutation CreateSubscription($input: SubscriptionInput!) {
    createSubscription(input: $input) {
      id
      status
      client {
        id
        fName
        lName
      }
      plan {
        name
        amount
        interval
      }
      startDate
      nextBillingDate
    }
  }
`;

export const CANCEL_SUBSCRIPTION = gql`
  mutation CancelSubscription($subscriptionId: ID!, $reason: String) {
    cancelSubscription(subscriptionId: $subscriptionId, reason: $reason) {
      id
      status
      canceledAt
    }
  }
`;

// === PAYMENT METHOD QUERIES ===
export const GET_PAYMENT_METHODS = gql`
  query GetPaymentMethods {
    paymentMethods {
      id
      client {
        id
        fName
        lName
        email
      }
      type
      isDefault
      status
      stripePaymentMethodId
      stripeCustomerId
      cardDetails {
        brand
        last4
        expMonth
        expYear
      }
      description
      lastUsedAt
      createdAt
    }
  }
`;

export const GET_PAYMENT_METHODS_BY_CLIENT = gql`
  query GetPaymentMethodsByClient($clientId: ID!) {
    paymentMethodsByClient(clientId: $clientId) {
      id
      type
      isDefault
      status
      stripePaymentMethodId
      stripeCustomerId
      cardDetails {
        brand
        last4
        expMonth
        expYear
      }
      description
      lastUsedAt
      createdAt
    }
  }
`;

export const CREATE_PAYMENT_METHOD = gql`
  mutation CreatePaymentMethod($input: PaymentMethodInput!) {
    createPaymentMethod(input: $input) {
      id
      client {
        id
        fName
        lName
        email
      }
      type
      status
      isDefault
      stripePaymentMethodId
      stripeCustomerId
      cardDetails {
        brand
        last4
        expMonth
        expYear
      }
      description
      createdAt
    }
  }
`;

export const DELETE_PAYMENT_METHOD = gql`
  mutation DeletePaymentMethod($id: ID!) {
    deletePaymentMethod(id: $id)
  }
`;

// === MANUAL CHARGING MUTATIONS (NEW) ===
export const CHARGE_PAYMENT_METHOD = gql`
  mutation ChargePaymentMethod(
    $paymentMethodId: ID!
    $amount: Float!
    $description: String!
    $clientId: ID!
    $currency: String = "USD"
  ) {
    chargePaymentMethod(
      paymentMethodId: $paymentMethodId
      amount: $amount
      description: $description
      clientId: $clientId
      currency: $currency
    )
  }
`;

export const CREATE_MANUAL_CHARGE = gql`
  mutation CreateManualCharge($input: ManualChargeInput!) {
    createManualCharge(input: $input) {
      id
      client {
        id
        fName
        lName
        email
      }
      amount
      currency
      description
      status
      chargeId
      receiptUrl
      paymentMethod {
        id
        cardDetails {
          brand
          last4
        }
      }
      createdAt
    }
  }
`;

// === CHARGE HISTORY QUERIES ===
export const GET_CHARGES = gql`
  query GetCharges {
    charges {
      id
      client {
        id
        fName
        lName
        email
      }
      total
      currency
      lineItems {
        description
        quantity
        unitPrice
        amount
      }
      status
      stripePaymentIntentId
      invoiceNumber
      createdAt
    }
  }
`;

export const GET_CHARGES_BY_CLIENT = gql`
  query GetChargesByClient($clientId: ID!) {
    chargesByClient(clientId: $clientId) {
      id
      total
      currency
      lineItems {
        description
        quantity
        unitPrice
        amount
      }
      status
      stripePaymentIntentId
      invoiceNumber
      createdAt
    }
  }
`;

// === SETUP INTENTS ===
export const CREATE_SETUP_INTENT = gql`
  mutation CreateSetupIntent($clientId: String!) {
    createSetupIntent(clientId: $clientId)
  }
`;

// === SUBSCRIPTION BILLING MUTATIONS ===

/**
 * Issue a bill for one subscription.
 *
 * `asDraft` is the difference between "raise the bill" and "tell the customer":
 *   false (default) — creates it at SENT and EMAILS the client an invoice
 *   true            — creates it at DRAFT and sends NOTHING; you send it yourself
 */
export const ISSUE_SINGLE_SUBSCRIPTION_BILL = gql`
  mutation IssueSingleSubscriptionBill(
    $subscriptionId: ID!
    $asDraft: Boolean
    # The period the bill COVERS. TMS bills in advance, so a bill raised at the
    # start of September covers September. Omitted = the current Brisbane month.
    $billingPeriod: String
    # Deliberate override of one-bill-per-subscription-per-period, set only
    # after the operator has read the warning.
    $allowDuplicate: Boolean
  ) {
    issueSingleSubscriptionBill(
      subscriptionId: $subscriptionId
      asDraft: $asDraft
      billingPeriod: $billingPeriod
      allowDuplicate: $allowDuplicate
    ) {
      id
      isPaid
      status
      totalAmount
      billingPeriod
      dueDate
      clientId
    }
  }
`;

export const ISSUE_SUBSCRIPTION_BILLS = gql`
  mutation IssueSubscriptionBills($billingPeriod: String) {
    issueSubscriptionBills(billingPeriod: $billingPeriod) {
      success
      totalProcessed
      successCount
      errorCount
      skippedCount
      totalAmountBilled
      results {
        subscriptionId
        clientName
        success
        amount
        billId
        error
        skippedReason
      }
    }
  }
`;

export const CHARGE_UNPAID_SUBSCRIPTION_BILLS = gql`
  mutation ChargeUnpaidSubscriptionBills($billingPeriod: String) {
    chargeUnpaidSubscriptionBills(billingPeriod: $billingPeriod) {
      success
      totalProcessed
      successCount
      errorCount
      skippedCount
      totalAmountBilled
      results {
        subscriptionId
        clientName
        success
        amount
        billId
        error
        skippedReason
      }
    }
  }
`;

// === PRO-RATA BILLING ===

export const PRO_RATA_PREVIEW = gql`
  query ProRataPreview($subscriptionId: ID!, $startDate: String!, $endDate: String!) {
    proRataPreview(subscriptionId: $subscriptionId, startDate: $startDate, endDate: $endDate) {
      planName
      planAmount
      startDate
      endDate
      daysInMonth
      proRataDays
      dailyRate
      subtotal
      taxPercentage
      taxAmount
      totalAmount
    }
  }
`;

export const ISSUE_PRO_RATA_BILL = gql`
  mutation IssueProRataBill($subscriptionId: ID!, $startDate: String!, $endDate: String!) {
    issueProRataBill(subscriptionId: $subscriptionId, startDate: $startDate, endDate: $endDate) {
      id
      isPaid
      status
      totalAmount
      billingPeriod
      dueDate
      clientId
      lineItems {
        description
        amount
      }
      subtotal
      taxPercentage
      taxAmount
    }
  }
`;

// === SUBSCRIPTION PRODUCTS QUERY (for CreateSubscription) ===

export const GET_SUBSCRIPTION_PRODUCTS = gql`
  query SubscriptionProducts {
    subscriptionProducts {
      id name slug description amount interval currency
      features badge color isRecommended sortOrder isActive tier
      includedModules ctaText trialDays chargeOnActivation
    }
  }
`;

/** Get subscription products with ALL fields (for admin SubscriptionProducts page) */
export const GET_SUBSCRIPTION_PRODUCTS_ADMIN = gql`
  query SubscriptionProducts {
    subscriptionProducts {
      id name slug description detailedDescription amount interval currency
      features badge color isRecommended sortOrder isActive tier
      includedModules stripePriceId stripeProductId ctaText trialDays
      linkedCourseId salesPageUrl courseStartUrl
      welcomeEmailSubject welcomeEmailBody chargeOnActivation grantedPermissions
    }
  }
`;

/**
 * Loose pull of every ModuleSource row so the SubscriptionProducts page can
 * group modules by tier for the hover tooltip on the Tier badge. Admin-only
 * (matches the page's existing gate); errors silently for non-admins.
 */
export const GET_MODULE_SOURCES_FOR_TIER = gql`
  query ModuleSourcesForTier {
    moduleSources {
      moduleId
      tier
    }
  }
`;

/** Get LM courses for product selector */
export const GET_LM_COURSES_FOR_SELECTOR = gql`
  query LmGetCourses {
    lmGetCourses { id title courseSlug isPublished }
  }
`;

/** Get tenant domain and payment settings */
export const GET_TENANT_DOMAIN = gql`
  query GetTenantDomain($id: ID!) {
    tenant(id: $id) {
      id domain skipPaymentCollection
      apiKeys { stripePublicKey stripeSecretKey }
    }
  }
`;

/** Update tenant payment collection setting */
export const UPDATE_TENANT_PAYMENT_SETTING = gql`
  mutation UpdateTenantPaymentSetting($id: ID!, $input: UpdateTenantInput!) {
    updateTenant(id: $id, input: $input) {
      id skipPaymentCollection
    }
  }
`;

/** Create a subscription product */
export const CREATE_SUBSCRIPTION_PRODUCT = gql`
  mutation CreateSubscriptionProduct($input: SubscriptionProductInput!) {
    createSubscriptionProduct(input: $input) {
      id name slug amount
    }
  }
`;

/** Update a subscription product */
export const UPDATE_SUBSCRIPTION_PRODUCT = gql`
  mutation UpdateSubscriptionProduct($id: ID!, $input: UpdateSubscriptionProductInput!) {
    updateSubscriptionProduct(id: $id, input: $input) {
      id name slug amount
    }
  }
`;

/** Delete a subscription product */
export const DELETE_SUBSCRIPTION_PRODUCT = gql`
  mutation DeleteSubscriptionProduct($id: ID!) {
    deleteSubscriptionProduct(id: $id)
  }
`;

// === SUBSCRIBE PAGE QUERIES (public-facing) ===

/** Get a subscription product by slug (public) */
export const GET_PRODUCT_BY_SLUG = gql`
  query PublicSubscriptionProductBySlug($tenantId: String!, $slug: String!) {
    publicSubscriptionProductBySlug(tenantId: $tenantId, slug: $slug) {
      id name slug description detailedDescription amount interval currency
      features badge color isRecommended tier ctaText trialDays
      linkedCourseId salesPageUrl courseStartUrl chargeOnActivation
    }
  }
`;

/** Get all subscription products (public) */
export const GET_ALL_PRODUCTS_PUBLIC = gql`
  query PublicSubscriptionProducts($tenantId: String!) {
    publicSubscriptionProducts(tenantId: $tenantId) {
      id name slug amount interval currency badge chargeOnActivation
    }
  }
`;

/** Get current user's payment methods */
export const GET_MY_PAYMENT_METHODS = gql`
  query MyPaymentMethods {
    myPaymentMethods {
      id isDefault stripePaymentMethodId
      cardDetails { brand last4 }
    }
  }
`;

/** Get tenant's Stripe public key */
export const GET_TENANT_STRIPE_PUBLIC_KEY = gql`
  query GetTenantStripePublicKey {
    getTenantStripePublicKey
  }
`;

/** Create a local subscription (activate). Optional couponCode discounts the
 *  amount; a 100%-off / fully-covering coupon takes it to $0 (skips Stripe). */
export const CREATE_LOCAL_SUBSCRIPTION = gql`
  mutation CreateLocalSubscription($subscriptionProductId: ID!, $couponCode: String, $tokenCount: Int) {
    createLocalSubscription(subscriptionProductId: $subscriptionProductId, couponCode: $couponCode, tokenCount: $tokenCount) {
      success subscriptionId enrollmentId courseStartUrl error
    }
  }
`;

/** Validate a coupon at checkout (preview the discount before subscribing). */
export const VALIDATE_COUPON = gql`
  query ValidateCoupon($code: String!, $productId: ID) {
    validateCoupon(code: $code, productId: $productId) {
      valid reason code discountType discountValue discountApplied finalAmount
    }
  }
`;

// === EDIT SUBSCRIPTION ===

/** Update a subscription */
export const UPDATE_SUBSCRIPTION = gql`
  mutation UpdateSubscription($id: ID!, $input: UpdateSubscriptionInput!) {
    updateSubscription(id: $id, input: $input) {
      id
      status
      plan {
        name
        description
        amount
        interval
        tier
      }
      nextBillingDate
      endDate
      cancellationReason
      notes
    }
  }
`;

// === STRIPE BILL PAYMENT (components/) ===

/** Query current user's payment methods for bill payment */
export const MY_PAYMENT_METHODS = gql`
  query MyPaymentMethods {
    myPaymentMethods {
      id isDefault stripePaymentMethodId
      cardDetails { brand last4 expMonth expYear }
    }
  }
`;

/** Admin query: fetch a specific client's payment methods for bill payment */
export const PAYMENT_METHODS_BY_CLIENT = gql`
  query PaymentMethodsByClient($clientId: ID!) {
    paymentMethodsByClient(clientId: $clientId) {
      id isDefault stripePaymentMethodId
      cardDetails { brand last4 expMonth expYear }
    }
  }
`;

/**
 * The subscription detail page's single read.
 *
 * Every derived value here — the billing state, the day labels, the overdue
 * count, the period label — is computed on the SERVER, Brisbane-anchored. The
 * page renders words around them and never does date arithmetic of its own:
 * misreading `nextBillingDate` is the reason this page exists, and a
 * browser-local clock would reintroduce the same class of mistake.
 */
export const SUBSCRIPTION_DETAIL = gql`
  query SubscriptionDetail($id: ID!) {
    subscriptionDetail(id: $id) {
      billingState
      dueDayBrisbane
      daysOverdue
      daysUntilDue
      currentPeriod
      billedThisPeriod
      autoChargeActive
      subscription {
        id
        status
        startDate
        endDate
        nextBillingDate
        billingMode
        notes
        plan { name description amount interval tier currency }
        client { id fName lName email phoneNumber businessName }
      }
      cardOnFile { id brand last4 expMonth expYear isDefault addedAt }
      billingHistory {
        billId
        billingPeriod
        amountTotal
        amountOutstanding
        paymentState
        issuedAt
        paidAt
        howPaid
        stripeDashboardUrl
      }
    }
  }
`;
